'use client'

import { useSyncExternalStore } from 'react'
import Image from 'next/image'

import { useScrollReveal } from '@/hooks/use-scroll-reveal'
import { TrendDelta } from '@/components/shared/trend-delta'

// ── Dark mode detection (class-based, SSR-safe) ───────────────────────────────

function subscribeDarkClass(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
  return () => observer.disconnect()
}

function useDarkMode(): boolean {
  return useSyncExternalStore(
    subscribeDarkClass,
    () => document.documentElement.classList.contains('dark'),
    () => false // SSR snapshot: assume light
  )
}

// ── HighlightMark ─────────────────────────────────────────────────────────────

interface HighlightMarkProps {
  color: 'teal' | 'orange' | 'brand'
  isVisible: boolean
  isDark: boolean
  delay: number
  bold?: boolean
  children: React.ReactNode
}

const HIGHLIGHT_COLORS = {
  teal: {
    text: 'text-[#0d9488] dark:text-[#2dd4bf]',
    bg: 'rgba(13,148,136,0.07)',
    bgDark: 'rgba(45,212,191,0.07)',
  },
  orange: {
    text: 'text-[#ea580c] dark:text-[#fb923c]',
    bg: 'rgba(234,88,12,0.06)',
    bgDark: 'rgba(251,146,60,0.06)',
  },
  // brand-token-migration 2026-08-22 correction: this highlights the tracked
  // brand's own mention ("YourBrand") — the same concept highlighted-response.tsx/
  // markdown-response.tsx/ai-response-excerpt.tsx render as emerald, not info/indigo.
  // Literal hex (not a CSS var) to match this object's teal/orange siblings; values
  // are --raw-positive (#17845a) / --d-positive (#4fc98d), --emerald's light/dark sources.
  brand: {
    text: 'text-emerald',
    bg: 'rgba(23,132,90,0.08)',
    bgDark: 'rgba(79,201,141,0.08)',
  },
}

function HighlightMark({ color, isVisible, isDark, delay, bold, children }: HighlightMarkProps) {
  const c = HIGHLIGHT_COLORS[color]
  return (
    <mark
      className={`rounded-sm px-0.5 not-italic ${c.text}${bold ? ' font-medium' : ''}`}
      style={{
        backgroundColor: isVisible ? (isDark ? c.bgDark : c.bg) : 'transparent',
        transition: 'background-color 120ms ease-out',
        transitionDelay: isVisible ? `${delay}ms` : '0ms',
      }}
    >
      {children}
    </mark>
  )
}

// ── MentionDetectionPanel ─────────────────────────────────────────────────────

export function MentionDetectionPanel() {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>()
  const isDark = useDarkMode()

  return (
    <div ref={ref} className="overflow-hidden rounded-[10px] border border-border bg-card">
      {/* Section A — Prompt header bar */}
      <div className="flex h-9 items-center justify-between border-b border-border bg-surface px-4">
        <span className="type-overline shrink-0 text-muted-foreground">Prompt</span>
        <span className="mx-3 min-w-0 flex-1 truncate text-body-sm font-medium text-foreground">
          best project management tool for SaaS teams
        </span>
        {/* Engine chip */}
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border px-2 py-0.5">
          <Image
            src="/images/ai-platforms/chatgpt-logo.svg"
            alt="ChatGPT"
            width={16}
            height={16}
            className="w-4 dark:brightness-0 dark:invert"
            style={{ height: 'auto' }}
          />
          <span className="text-caption text-muted-foreground">ChatGPT</span>
        </span>
      </div>

      {/* Section B — Response excerpt */}
      <div className="border-b border-border px-4 py-4 text-body leading-relaxed text-foreground">
        <p>
          For SaaS teams evaluating project management tools,{' '}
          <HighlightMark color="teal" isVisible={isVisible} isDark={isDark} delay={400}>
            Acme
          </HighlightMark>{' '}
          is popular for its lightweight interface.{' '}
          <HighlightMark color="orange" isVisible={isVisible} isDark={isDark} delay={600}>
            Linear
          </HighlightMark>{' '}
          appeals to engineering-led teams.{' '}
          <HighlightMark color="brand" isVisible={isVisible} isDark={isDark} delay={800} bold>
            YourBrand
          </HighlightMark>{' '}
          earns mentions for cross-functional teams needing one tool for product and marketing.
        </p>
      </div>

      {/* Section C — Rank indicator row */}
      <div className="flex h-8 items-center justify-between bg-surface px-4">
        <span className="text-body-sm font-medium text-foreground">
          Rank <span className="text-primary-900">#2</span>
          {' · 3 brands mentioned'}
        </span>
        <TrendDelta value={1} format="count" comparison="last week" />
      </div>
    </div>
  )
}
